import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import classnames from "classnames";
import { toast } from "react-toastify";
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
  Form,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";

import { getOnePatient, updatePatient, getClinic, getClinicById, getmedprovider, getPlan, getStream } from "../../service/api";
import { jwtDecode } from "jwt-decode";

// ─── Updatepatient ──────────────────────────────────────────────────────────────
const Updatepatient = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [patient, setPatient] = useState(null); // null until loaded
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [nationality, setNationality] = useState("South African");
  const [mmnationality, setmmNationality] = useState("South African");

  const [customActiveTab, setcustomActiveTab] = useState("1");
  const [showMedicalFund, setShowMedicalFund] = useState(false);

  const [allcliniclist, setallclinic] = useState([]);
  const [med_provider, setmed_provider] = useState({ Med_provider: [] });
  const [plan, setPlan] = useState({ plan: [] });
  const [clinicID, setClinicID] = useState(null);
  const [str, setStr] = useState([])
  // Mobile input display state
  const [inputValue, setInputValue] = useState("");
  const [inputValueAlter, setInputAlterValue] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [mobileAlterError, setMobileAlterError] = useState("");

  // Signature
  const [signatureRef, setSignatureRef] = useState(null);

  // Address autocomplete ref
  const addressRef = useRef(null);

  // Gender radio refs
  const maleRef = useRef(null);
  const femaleRef = useRef(null);
  const nonbinaryRef = useRef(null);
  const mmmaleRef = useRef(null);
  const mmfemaleRef = useRef(null);
  const mmnonbinaryRef = useRef(null);

  // Validation errors
  const [errors, setErrors] = useState({
    title: false,
    firstname: false,
    lastname: false,
    age: false,
    mobile: false,
    City: false,
  });

  const today = new Date().toISOString().split("T")[0];

  // ── Fetch patient data on mount ─────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      setClinicID(decoded?.clinicID || null);
    }
    fetchPatient();
    getallclinic();
    getAllmedprovider();
    getAllplan();
    getStreamData();
  }, []);

  const getStreamData = async () => {
    let response = await getStream()
    setStr(response.data)
  }

  const fetchPatient = async () => {
    try {
      const response = await getOnePatient(id);
      const data = response.data;
      setPatient(data);

      // Restore nationality radio state
      if (data.nationality) setNationality(data.nationality);
      if (data.mainmemberdetail?.mmnationality) setmmNationality(data.mainmemberdetail.mmnationality);

      // Restore mobile display values
      if (data.mobile) {
        setInputValue(data.mobile);
      }
      if (data.altNumber) {
        setInputAlterValue(data.altNumber);
      }

      // Show medical fund tab if fund was "yes"
      if (data.fund === "yes") setShowMedicalFund(true);
    } catch (error) {
      console.error("Error fetching patient:", error);
      toast.error("Failed to load patient data.");
    } finally {
      setLoading(false);
    }
  };

  const getallclinic = async () => {
    try {
      let response;
      const token = localStorage.getItem("token");
      const decoded = token ? jwtDecode(token) : null;
      const cid = decoded?.clinicID || null;
      if (cid) {
        response = await getClinicById(cid);
        setallclinic([response.data]);
      } else {
        response = await getClinic();
        setallclinic(response.data);
      }
    } catch (err) {
      console.error("Error fetching clinic(s)", err);
    }
  };

  const getAllmedprovider = async () => {
    try {
      const response = await getmedprovider();
      if (response.data?.Med_provider) {
        setmed_provider(response.data);
      } else {
        setmed_provider({ Med_provider: [] });
      }
    } catch (error) {
      console.error("Error while fetching med providers:", error);
      setmed_provider({ Med_provider: [] });
    }
  };

  const getAllplan = async () => {
    try {
      const response = await getPlan();
      if (response.data?.plan) {
        setPlan(response.data);
      } else {
        setPlan({ plan: [] });
      }
    } catch (error) {
      console.error("Error while fetching plans:", error);
      setPlan({ plan: [] });
    }
  };

  // ── Sync gender radio buttons when patient loads ────────────────────────────
  useEffect(() => {
    if (!patient) return;
    if (patient.gender === "Male" && maleRef.current) maleRef.current.checked = true;
    else if (patient.gender === "Female" && femaleRef.current) femaleRef.current.checked = true;
    else if (patient.gender === "nonbinary" && nonbinaryRef.current) nonbinaryRef.current.checked = true;
  }, [patient?.gender]);

  useEffect(() => {
    if (!patient) return;
    const mmg = patient.mainmemberdetail?.mmgender;
    if (mmg === "Male" && mmmaleRef.current) mmmaleRef.current.checked = true;
    else if (mmg === "Female" && mmfemaleRef.current) mmfemaleRef.current.checked = true;
    else if (mmg === "nonbinary" && mmnonbinaryRef.current) mmnonbinaryRef.current.checked = true;
  }, [patient?.mainmemberdetail?.mmgender]);

  // ── Google Maps address autocomplete ───────────────────────────────────────
  useEffect(() => {
    const initAutocomplete = () => {
      if (!addressRef.current) return;
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressRef.current,
        { types: ["geocode"], componentRestrictions: { country: "ZA" } }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;
        let suburb = "", city = "", areaCode = "";
        place.address_components.forEach(component => {
          const types = component.types;
          if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
            suburb = component.long_name;
          }
          if (types.includes("locality")) city = component.long_name;
          if (types.includes("postal_code")) areaCode = component.long_name;
        });
        setPatient(prev => ({
          ...prev,
          address: place.formatted_address || "",
          suburb,
          city,
          acode: areaCode,
        }));
      });
    };
    if (window.google && window.google.maps && window.google.maps.places) {
      initAutocomplete();
    } else {
      const interval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(interval);
          initAutocomplete();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [loading]); // re-init after patient loads

  // ── Handlers ───────────────────────────────────────────────────────────────
  const onValueChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("mm")) {
      setPatient(prev => ({
        ...prev,
        mainmemberdetail: { ...prev.mainmemberdetail, [name]: value },
      }));
    } else {
      setPatient(prev => ({ ...prev, [name]: value }));
    }

    if (name === "fund") {
      setShowMedicalFund(value === "yes");
    }

    if (name === "idnumber" && /^\d*$/.test(value) && value.length <= 13) {
      calculateDOBAndGender(value);
    }
    if (name === "mmidnumber" && /^\d*$/.test(value) && value.length <= 13) {
      mmcalculateDOBAndGender(value);
    }
  };

  const onDateChange = (e) => {
    const { value } = e.target;
    calculateAge(value);
    setPatient(prev => ({ ...prev, dob: value }));
  };

  const onmmDateChange = (e) => {
    const { value } = e.target;
    const mmage = mmcalculateAge(value);
    setPatient(prev => ({
      ...prev,
      mainmemberdetail: { ...prev.mainmemberdetail, mmdob: value, mmage },
    }));
  };

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) age--;
    setPatient(prev => ({ ...prev, age: age.toString() }));
  };

  const mmcalculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let mmage = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) mmage--;
    return mmage.toString();
  };

  const calculateDOBAndGender = (idNumber) => {
    if (idNumber.length === 13) {
      const year = idNumber.substring(0, 2);
      const month = idNumber.substring(2, 4);
      const day = idNumber.substring(4, 6);
      const currentYear = new Date().getFullYear().toString().substring(2, 4);
      const fullYear = parseInt(year, 10) + (parseInt(year, 10) <= parseInt(currentYear) ? 2000 : 1900);
      const dob = `${fullYear}-${month}-${day}`;
      const gender = idNumber.charAt(6) < "5" ? "Female" : "Male";
      setPatient(prev => ({ ...prev, dob, gender }));
      calculateAge(dob);
    }
  };

  const mmcalculateDOBAndGender = (mmidnumber) => {
    if (mmidnumber.length === 13) {
      const year = mmidnumber.substring(0, 2);
      const month = mmidnumber.substring(2, 4);
      const day = mmidnumber.substring(4, 6);
      const currentYear = new Date().getFullYear().toString().substring(2, 4);
      const fullYear = parseInt(year, 10) + (parseInt(year, 10) <= parseInt(currentYear) ? 2000 : 1900);
      const mmdob = `${fullYear}-${month}-${day}`;
      const mmgender = mmidnumber.charAt(6) < "5" ? "Female" : "Male";
      setPatient(prev => ({
        ...prev,
        mainmemberdetail: {
          ...prev.mainmemberdetail,
          mmdob,
          mmgender,
          mmage: mmcalculateAge(mmdob),
        },
      }));
    }
  };

  const onMobileChange = (e) => {
    let value = e.target.value;
    if (!value.startsWith("27")) {
      setMobileError("Mobile number must start with 27");
      value = "27" + value;
    } else {
      setMobileError("");
    }
    setInputValue(value);
    const formatted = value.replace(/^27+0*/, "27");
    setPatient(prev => ({ ...prev, mobile: formatted }));
  };

  const onMobileChangeAlter = (e) => {
    let value = e.target.value;
    if (!value.startsWith("27")) {
      setMobileAlterError("Mobile number must start with 27");
      value = "27" + value;
    } else {
      setMobileAlterError("");
    }
    setInputAlterValue(value);
    const formatted = value.replace(/^27+0*/, "27");
    setPatient(prev => ({ ...prev, altNumber: formatted }));
  };

  const onNationalityChange = (e) => {
    setNationality(e.target.value);
    setPatient(prev => ({ ...prev, nationality: e.target.value }));
  };


  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) setcustomActiveTab(tab);
  };

  const clearSignature = () => {
    if (signatureRef) signatureRef.clear();
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const validationErrors = {
      City: !patient.city,
      title: !patient.title,
      firstname: !patient.firstname,
      lastname: !patient.lastname,
      age: !patient.age,
      mobile: !patient.mobile,
    };
    setErrors(validationErrors);

    if (Object.values(validationErrors).some(Boolean)) {
      toast.error("Please fill out all required fields before saving.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      // Append all patient fields
      Object.keys(patient).forEach((key) => {
        if (key === "mainmemberdetail" && typeof patient[key] === "object") {
          formData.append(key, JSON.stringify(patient[key]));
        } else if (key === "clinicname" && typeof patient[key] === "object" && patient[key]?._id) {
          // clinicname may be a populated object from getOnePatient - send only the ID
          formData.append(key, patient[key]._id);
        } else {
          formData.append(key, patient[key] ?? "");
        }
      });

      // Append new signature if available
      if (signatureRef && !signatureRef.isEmpty()) {
        formData.append("signature", signatureRef.toDataURL());
      }

      const response = await updatePatient(id, formData);
      console.log("Patient updated:", response.data);

      toast.success("Patient updated successfully!", { autoClose: 2000 });
      setTimeout(() => navigate("/Patient"), 2000);
    } catch (error) {
      console.error("Error updating patient:", error);
      toast.error("Failed to update patient. Check console for details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Card top style (same as Addpatient) ───────────────────────────────────
  const cardTopStyle = {
    backgroundColor: "#DC4C64",
    padding: "6px",
    color: "white",
    height: "30px",
  };

  // ── Loading / not-found guard ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <p>Loading patient data...</p>
        </Container>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="page-content">
        <Container fluid>
          <p>Patient not found.</p>
          <Link to="/Patient" className="btn btn-danger">Back to Patients</Link>
        </Container>
      </div>
    );
  }

  document.title = "Edit Patient | Form";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="page-title">Edit Patient</h4>
            <Link to="/Patient" className="btn btn-danger">
              View Patients
            </Link>
          </div>

          <Row>
            <Card>
              <CardBody>
                <Form>
                  <Row>
                    {/* Medical Fund radio */}
                    <Col md={12} className="mb-3">
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "20px", padding: "8px 16px", backgroundColor: "#fff5f7", borderRadius: "6px", border: "1px solid #f5c6cb" }}>
                        <span style={{ fontWeight: "600", color: "#DC4C64", fontSize: "13px", marginRight: "4px" }}>Medical Fund :</span>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div className="form-check form-radio-danger" style={{ marginRight: "10px" }}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="fund"
                              id="fund_yes"
                              value="yes"
                              defaultChecked={patient.fund === "yes"}
                              onChange={onValueChange}
                            />
                            <label className="form-check-label" htmlFor="fund_yes" style={{ color: "#212529", fontWeight: "500" }}>Yes</label>
                          </div>
                          <div className="form-check form-radio-danger" style={{ marginRight: "10px" }}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="fund"
                              id="fund_no"
                              value="no"
                              defaultChecked={patient.fund === "no"}
                              onChange={onValueChange}
                            />
                            <label className="form-check-label" htmlFor="fund_no" style={{ color: "#212529", fontWeight: "500" }}>No</label>
                          </div>
                        </div>
                      </div>
                    </Col>

                    {/* Tabs */}
                    <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({ active: customActiveTab === "1" })}
                          onClick={() => toggleCustom("1")}
                        >
                          <span className="d-none d-sm-block">Patient Details</span>
                        </NavLink>
                      </NavItem>
                      {showMedicalFund && (
                        <NavItem>
                          <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({ active: customActiveTab === "2" })}
                            onClick={() => toggleCustom("2")}
                          >
                            <span className="d-none d-sm-block">Patient Medical Aid Details</span>
                          </NavLink>
                        </NavItem>
                      )}
                      {showMedicalFund && (
                        <NavItem>
                          <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({ active: customActiveTab === "3" })}
                            onClick={() => toggleCustom("3")}
                          >
                            <span className="d-none d-sm-block">Main Member Details</span>
                          </NavLink>
                        </NavItem>
                      )}
                    </Nav>
                  </Row>

                  {/* ── Tab Content ──────────────────────────────────────── */}
                  <TabContent activeTab={customActiveTab} className="p-3 text-muted">

                    {/* ── Tab 2: Medical Aid ─────────────────────────────── */}
                    <TabPane tabId="2">
                      <Row>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Medical Aid Name</Label>
                            <select name="maidname" className="form-control" value={patient.maidname || ""} onChange={onValueChange}>
                              <option value="">Select Medical Aid</option>
                              {Array.isArray(med_provider.Med_provider) &&
                                med_provider.Med_provider.map((item, index) => (
                                  <option key={index} value={item.med_provider}>{item.med_provider}</option>
                                ))}
                            </select>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Plan</Label>
                            <select name="mplan" className="form-control" value={patient.mplan || ""} onChange={onValueChange}>
                              <option value="">Select Plan</option>
                              {Array.isArray(plan.plan) &&
                                plan.plan.map((item, index) => (
                                  <option key={index} value={item.plan}>{item.plan}</option>
                                ))}
                            </select>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Medical Fund Number</Label>
                            <Input type="text" className="form-control" name="mfno" value={patient.mfno || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Amount</Label>
                            <Input type="text" className="form-control" name="mamount" value={patient.mamount || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Dependant Code</Label>
                            <Input type="text" className="form-control" name="mdepcode" value={patient.mdepcode || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* ── Tab 3: Main Member ─────────────────────────────── */}
                    <TabPane tabId="3">
                      <Row>
                        <Col md={1}>
                          <div className="mb-3">
                            <Label>Title</Label>
                            <select name="mmtitle" className="form-control" value={patient.mainmemberdetail?.mmtitle || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              <option>Mr</option><option>Ms</option><option>Mrs</option><option>Dr</option><option>Prof</option>
                            </select>
                          </div>
                        </Col>
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>First Name</Label>
                            <Input type="text" name="mmfname" value={patient.mainmemberdetail?.mmfname || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>Last Name</Label>
                            <Input type="text" name="mmlname" value={patient.mainmemberdetail?.mmlname || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={5}>
                          <Label>Nationality</Label>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            {["South African", "Non-South African", "Non-Documented"].map((val) => (
                              <button
                                key={val}
                                type="button"
                                onClick={() => { setmmNationality(val); setPatient(prev => ({ ...prev, mainmemberdetail: { ...prev.mainmemberdetail, mmnationality: val } })); }}
                                style={{
                                  padding: "2px 9px", borderRadius: "20px", cursor: "pointer",
                                  border: `1.5px solid ${mmnationality === val ? "#DC4C64" : "#ced4da"}`,
                                  background: mmnationality === val ? "#fff0f3" : "#fff",
                                  fontSize: "11px", fontWeight: mmnationality === val ? "600" : "500",
                                  color: mmnationality === val ? "#DC4C64" : "#333",
                                  transition: "all 0.15s", userSelect: "none",
                                }}
                              >
                                {val}
                              </button>
                            ))}
                          </div>
                        </Col>
                        {mmnationality === "South African" && (
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>ID Number</Label>
                              <Input
                                type="text"
                                name="mmidnumber"
                                maxLength="13"
                                value={patient.mainmemberdetail?.mmidnumber || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d*$/.test(val) && val.length <= 13) onValueChange(e);
                                }}
                              />
                            </div>
                          </Col>
                        )}
                        {mmnationality === "Non-South African" && (
                          <>
                            <Col md={4}>
                              <div className="mb-3">
                                <Label>Passport Number</Label>
                                <Input type="text" name="mmpassportNumber" maxLength={9} value={patient.mainmemberdetail?.mmpassportNumber || ""} onChange={onValueChange} />
                              </div>
                            </Col>
                            <Col md={2}>
                              <div className="mb-3">
                                <Label>Expiry Date</Label>
                                <Input type="date" name="mmexpiryDate" min={today} value={patient.mainmemberdetail?.mmexpiryDate || ""} onChange={onValueChange} />
                              </div>
                            </Col>
                          </>
                        )}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Date Of Birth</Label>
                            <Input type="date" name="mmdob" value={patient.mainmemberdetail?.mmdob || ""} onChange={onmmDateChange} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Age</Label>
                            <Input type="text" name="mmage" value={patient.mainmemberdetail?.mmage || ""} readOnly />
                          </div>
                        </Col>
                        {/* MM Gender */}
                        <Col md={3}>
                          <Label>Gender</Label>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            {[
                              { id: "mmgMale", value: "Male", ref: mmmaleRef, label: "Male" },
                              { id: "mmgFemale", value: "Female", ref: mmfemaleRef, label: "Female" },
                              { id: "mmgNonbinary", value: "nonbinary", ref: mmnonbinaryRef, label: "Nonbinary" },
                            ].map(({ id, value, ref, label }) => (
                              <label key={id} htmlFor={id} style={{
                                display: "inline-flex", alignItems: "center", gap: "6px",
                                padding: "2px 9px", borderRadius: "20px", cursor: "pointer",
                                border: `1.5px solid ${patient.mainmemberdetail?.mmgender === value ? "#DC4C64" : "#ced4da"}`,
                                background: patient.mainmemberdetail?.mmgender === value ? "#fff0f3" : "#fff",
                                fontSize: "11px", fontWeight: "500", color: "#333",
                                transition: "all 0.15s", userSelect: "none",
                              }}>
                                <input
                                  type="radio" id={id} name="mmgender" value={value}
                                  ref={ref}
                                  onChange={e => onValueChange(e)}
                                  style={{ accentColor: "#DC4C64", width: "13px", height: "13px", cursor: "pointer" }}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label>Religion</Label>
                            <select name="mmreligion" className="form-control" value={patient.mainmemberdetail?.mmreligion || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Animism", "Buddhism", "Christianity", "Hinduism", "Islam", "Judaism", "Sikhism", "Traditional African", "Unknown"].map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                        </Col>
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Citizenship</Label>
                            <select name="mmcitizenship" className="form-control" value={patient.mainmemberdetail?.mmcitizenship || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Angola", "Botswana", "Comoros", "Democratic Republic of Congo(DRC)", "Lesotho", "Madagascar", "Malawi", "Mauritius", "Mozambique", "Nambia", "Seychelles", "South Africa", "Swziland", "United Republic of Tanzania", "Zambia", "Zimbabwe", "Other"].map(c => <option key={c}>{c}</option>)}
                            </select>
                          </div>
                        </Col>
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Language</Label>
                            <select name="mmlanguage" className="form-control" value={patient.mainmemberdetail?.mmlanguage || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Afrikaans", "English", "Isindebele", "Isixhosa", "Isizulu", "Other", "Sepedi", "Sesotho", "Setswana", "Siswati", "Tshivenda", "Unknown", "Xitsonga"].map(l => <option key={l}>{l}</option>)}
                            </select>
                          </div>
                        </Col>
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Race</Label>
                            <select name="mmrace" className="form-control" value={patient.mainmemberdetail?.mmrace || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Black", "Coloured", "Indian", "Other", "White"].map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Mobile Number</Label>
                            <Input type="text" name="mmmobile" maxLength={11} value={patient.mainmemberdetail?.mmmobile || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Email</Label>
                            <Input type="email" name="mmemail" value={patient.mainmemberdetail?.mmemail || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Alternative Number</Label>
                            <Input type="text" name="mmaltNumber" maxLength={11} value={patient.mainmemberdetail?.mmaltNumber || ""} onChange={onValueChange} />
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* ── Tab 1: Patient Details ─────────────────────────── */}
                    <TabPane tabId="1">
                      <Row>
                        {/* Practice / Clinic — hidden for clinic users (clinicID set), shown only for admin */}
                        {!clinicID && (
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Practice Name</Label>
                              <select
                                name="clinicname"
                                className="form-control"
                                value={
                                  typeof patient.clinicname === "object"
                                    ? patient.clinicname?._id
                                    : patient.clinicname || ""
                                }
                                onChange={onValueChange}
                              >
                                <option value="">Choose...</option>
                                {allcliniclist.map((clinic) => (
                                  <option key={clinic._id} value={clinic._id}>{clinic.name}</option>
                                ))}
                              </select>
                            </div>
                          </Col>
                        )}

                        {/* Patient Category */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Patient Category</Label>
                            <select name="patientcate" className="form-control" value={patient.patientcate || ""} onChange={onValueChange}>
                              <option defaultValue>Select</option>
                              <option>Child/Adolescent (0–19 years)</option>
                              <option>Adult (20–59 years)</option>
                              <option>Elder/Senior (60+ years)</option>
                            </select>
                          </div>
                        </Col>

                        {/* MPI No (read-only) */}
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>MPI No.</Label>
                            <Input type="text" name="mpino" value={patient.mpino || ""} readOnly />
                          </div>
                        </Col>

                        {/* File No */}
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>File No.</Label>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {/* Clinic code prefix (read-only) */}
                              {/* <Input
                                type="text"
                                value={
                                  allcliniclist.find(c =>
                                    c._id === (typeof patient.clinicname === "object" ? patient.clinicname?._id : patient.clinicname)
                                  )?.code?.substring(0, 5) || ""
                                }
                                readOnly
                                style={{ width: "65px", marginRight: "-1px", backgroundColor: "#f8f9fa", border: "1px solid #ced4da", textAlign: "center" }}
                              /> */}
                              <Input
                                type="text"
                                className="form-control"
                                name="fileno"
                                maxLength="5"
                                value={patient.fileno || ""}
                                style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", textAlign: "center" }}

                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d*$/.test(val)) onValueChange(e);
                                }}
                                placeholder="File No."
                              />
                            </div>
                          </div>
                        </Col>

                        {/* Classification */}
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Classification</Label>
                            <select name="sQuantityUnit" className="form-control" value={patient.sQuantityUnit || ""} onChange={onValueChange}>
                              {str.length > 0 ? (
                                <>
                                  <option defaultValue>Select</option>
                                  {str.map(s => (
                                    <option key={s._id} value={s._id}>
                                      {s.name}
                                    </option>
                                  ))}
                                </>
                              ) : (
                                <option disabled>No Classification available</option>
                              )}
                            </select>
                          </div>
                        </Col>

                        {/* Title */}
                        <Col lg={1}>
                          <div className="mb-3">
                            <Label>Title <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                            <select name="title" className="form-control" value={patient.title || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              <option>Mr</option><option>Ms</option><option>Mrs</option><option>Dr</option><option>Prof</option>
                            </select>
                            {errors.title && <span className="text-danger">*required</span>}
                          </div>
                        </Col>

                        {/* First Name */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>First Name <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                            <Input
                              type="text"
                              name="firstname"
                              value={patient.firstname || ""}
                              onChange={(e) => {
                                if (/^[a-zA-Z\s]*$/.test(e.target.value)) onValueChange(e);
                              }}
                            />
                            {errors.firstname && <span className="text-danger">*Please fill out this field</span>}
                          </div>
                        </Col>

                        {/* Last Name */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Last Name <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                            <Input
                              type="text"
                              name="lastname"
                              value={patient.lastname || ""}
                              onChange={(e) => {
                                if (/^[a-zA-Z\s]*$/.test(e.target.value)) onValueChange(e);
                              }}
                            />
                            {errors.lastname && <span className="text-danger">*Please fill out this field</span>}
                          </div>
                        </Col>

                        {/* Nationality */}
                        <Col md={5}>
                          <Label>Nationality</Label>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            {["South African", "Non-South African", "Non-Documented"].map((val) => (
                              <button
                                key={val}
                                type="button"
                                onClick={() => { setNationality(val); setPatient(prev => ({ ...prev, nationality: val })); }}
                                style={{
                                  padding: "2px 9px", borderRadius: "20px", cursor: "pointer",
                                  border: `1.5px solid ${nationality === val ? "#DC4C64" : "#ced4da"}`,
                                  background: nationality === val ? "#fff0f3" : "#fff",
                                  fontSize: "11px", fontWeight: nationality === val ? "600" : "500",
                                  color: nationality === val ? "#DC4C64" : "#333",
                                  transition: "all 0.15s", userSelect: "none",
                                }}
                              >
                                {val}
                              </button>
                            ))}
                          </div>
                        </Col>

                        {/* ID Number (South African) */}
                        {nationality === "South African" && (
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>ID Number</Label>
                              <Input
                                type="text"
                                name="idnumber"
                                maxLength="13"
                                value={patient.idnumber || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^\d*$/.test(val) && val.length <= 13) onValueChange(e);
                                }}
                              />
                            </div>
                          </Col>
                        )}

                        {/* Passport (Non-South African) */}
                        {nationality === "Non-South African" && (
                          <>
                            <Col md={4}>
                              <div className="mb-3">
                                <Label>Passport Number</Label>
                                <Input type="text" name="passportNumber" maxLength={9} value={patient.passportNumber || ""} onChange={onValueChange} />
                              </div>
                            </Col>
                            <Col md={2}>
                              <div className="mb-3">
                                <Label>Expiry Date</Label>
                                <Input type="date" name="expiryDate" min={today} value={patient.expiryDate || ""} onChange={onValueChange} />
                              </div>
                            </Col>
                          </>
                        )}

                        {/* DOB */}
                        <Col md={2}>
                          <div className="mb-3">
                            <Label>Date Of Birth</Label>
                            <Input type="date" name="dob" value={patient.dob || ""} onChange={onDateChange} />
                          </div>
                        </Col>

                        {/* Age */}
                        <Col md={1}>
                          <div className="mb-3">
                            <Label>Age <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                            <Input type="text" name="age" value={patient.age || ""} readOnly />
                            {errors.age && <span className="text-danger">*required</span>}
                          </div>
                        </Col>

                        {/* Gender */}
                        <Col md={3}>
                          <Label>Gender</Label>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            {[
                              { id: "gMale", value: "Male", ref: maleRef, label: "Male" },
                              { id: "gFemale", value: "Female", ref: femaleRef, label: "Female" },
                              { id: "gNonbinary", value: "nonbinary", ref: nonbinaryRef, label: "Nonbinary" },
                            ].map(({ id, value, ref, label }) => (
                              <label key={id} htmlFor={id} style={{
                                display: "inline-flex", alignItems: "center", gap: "6px",
                                padding: "2px 9px", borderRadius: "20px", cursor: "pointer",
                                border: `1.5px solid ${patient.gender === value ? "#DC4C64" : "#ced4da"}`,
                                background: patient.gender === value ? "#fff0f3" : "#fff",
                                fontSize: "11px", fontWeight: "500", color: "#333",
                                transition: "all 0.15s", userSelect: "none",
                              }}>
                                <input
                                  type="radio" id={id} name="gender" value={value}
                                  ref={ref}
                                  onChange={e => onValueChange(e)}
                                  style={{ accentColor: "#DC4C64", width: "13px", height: "13px", cursor: "pointer" }}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </Col>

                        {/* Religion */}
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label>Religion</Label>
                            <select name="religion" className="form-control" value={patient.religion || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Animism", "Buddhism", "Christianity", "Hinduism", "Islam", "Judaism", "Sikhism", "Traditional African", "Unknown"].map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                        </Col>

                        {/* Citizenship */}
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Citizenship</Label>
                            <select name="citizenship" className="form-control" value={patient.citizenship || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Angola", "Botswana", "Comoros", "Democratic Republic of Congo(DRC)", "Lesotho", "Madagascar", "Malawi", "Mauritius", "Mozambique", "Nambia", "Seychelles", "South Africa", "Swziland", "United Republic of Tanzania", "Zambia", "Zimbabwe", "Other"].map(c => <option key={c}>{c}</option>)}
                            </select>
                          </div>
                        </Col>

                        {/* Language */}
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Language</Label>
                            <select name="language" className="form-control" value={patient.language || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Afrikaans", "English", "Isindebele", "Isixhosa", "Isizulu", "Other", "Sepedi", "Sesotho", "Setswana", "Siswati", "Tshivenda", "Unknown", "Xitsonga"].map(l => <option key={l}>{l}</option>)}
                            </select>
                          </div>
                        </Col>

                        {/* Race */}
                        <Col lg={2}>
                          <div className="mb-3">
                            <Label>Race</Label>
                            <select name="race" className="form-control" value={patient.race || ""} onChange={onValueChange}>
                              <option value="">Select</option>
                              {["Black", "Coloured", "Indian", "Other", "White"].map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                        </Col>

                        {/* Mobile */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Mobile Number <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                            <Input
                              type="text"
                              name="mobile"
                              maxLength={11}
                              value={inputValue}
                              onKeyPress={(e) => { if (e.which < 48 || e.which > 57) e.preventDefault(); }}
                              onChange={onMobileChange}
                            />
                            {mobileError && <small className="text-danger">{mobileError}</small>}
                            {errors.mobile && <span className="text-danger">*Please fill out this field</span>}
                          </div>
                        </Col>

                        {/* Email */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Email</Label>
                            <Input type="email" name="email" value={patient.email || ""} onChange={onValueChange} />
                          </div>
                        </Col>

                        {/* Alt Number */}
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Alternative Number</Label>
                            <Input
                              type="text"
                              name="altNumber"
                              maxLength={11}
                              value={inputValueAlter}
                              onKeyPress={(e) => { if (e.which < 48 || e.which > 57) e.preventDefault(); }}
                              onChange={onMobileChangeAlter}
                            />
                            {mobileAlterError && <small className="text-danger">{mobileAlterError}</small>}
                          </div>
                        </Col>
                      </Row>

                      {/* ── Address Card ─────────────────────────────────── */}
                      <Card>
                        <CardBody>
                          <div style={cardTopStyle}>
                            <CardTitle className="h4">PATIENT ADDRESS</CardTitle>
                          </div>
                          <br /><br />
                          <Form>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label>Address</Label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="address"
                                    ref={addressRef}
                                    value={patient.address || ""}
                                    onChange={onValueChange}
                                    autoComplete="off"
                                  />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className="mb-3">
                                  <Label>Suburb</Label>
                                  <Input type="text" name="suburb" value={patient.suburb || ""} onChange={onValueChange} />
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className="mb-3">
                                  <Label>City <span style={{ color: "red", fontWeight: "bolder" }}>*</span></Label>
                                  <Input type="text" name="city" value={patient.city || ""} onChange={onValueChange} />
                                  {errors.City && <span className="text-danger">*Please fill out this field</span>}
                                </div>
                              </Col>
                              <Col md={2}>
                                <div className="mb-3">
                                  <Label>Area Code</Label>
                                  <Input
                                    type="text"
                                    name="acode"
                                    maxLength={5}
                                    value={patient.acode || ""}
                                    onKeyPress={(e) => { if (e.which < 48 || e.which > 57) e.preventDefault(); }}
                                    onChange={(e) => {
                                      if (/^\d*$/.test(e.target.value)) onValueChange(e);
                                    }}
                                  />
                                </div>
                              </Col>

                              {/* Digital Signature */}
                              <Col md={3}>
                                <div className="mb-3">
                                  <Label><b>Digital Signature</b></Label>
                                  <div className="signature-container">
                                    <SignatureCanvas
                                      penColor="black"
                                      canvasProps={{
                                        width: 200,
                                        height: 150,
                                        className: "signature-canvas",
                                        style: { border: "5px solid #ccc" },
                                      }}
                                      ref={(ref) => setSignatureRef(ref)}
                                    />
                                  </div>
                                  <div className="mt-3">
                                    <Button onClick={clearSignature} color="secondary" style={{ marginRight: "18px" }}>
                                      Clear
                                    </Button>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Form>
                        </CardBody>
                      </Card>
                    </TabPane>
                  </TabContent>
                </Form>

                {/* Save button */}
                <div className="text-end">
                  <button
                    className="btn btn-primary w-md mx-0"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </CardBody>
            </Card>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Updatepatient;
